<template>
  <div class="norce-checkout-dropin">
    <!-- Loading State (initializing or processing 3DS redirect) -->
    <div v-if="isInitializing || isProcessingRedirect" class="flex items-center justify-center py-32">
      <LoadingIcon />
      <span class="ml-12 type-sm text-text-subtle">
        {{ isProcessingRedirect ? $t('checkout.norceCheckout.completingPayment') : $t('checkout.norceCheckout.initializing') }}
      </span>
    </div>

    <!-- Error State (when no payment data available) -->
    <div v-else-if="error && !payment" class="p-16 bg-bg-error rounded-8">
      <p class="type-sm text-text-error">{{ error }}</p>
      <button
        class="btn btn--secondary mt-16"
        type="button"
        @click="retryInitialize"
      >
        {{ $t('checkout.norceCheckout.retry') }}
      </button>
    </div>

    <!-- Adyen Drop-in Container -->
    <div v-else v-show="payment && !isInitializing">
      <div class="adyen-dropin-wrapper relative">
        <div ref="dropinContainer" class="adyen-dropin-container" />

        <!-- Validation Overlay - blocks interaction until checkout requirements are met OR payment is locked after error -->
        <div
          v-if="!props.canCompletePurchase || paymentLocked"
          class="adyen-validation-overlay"
        />
      </div>

      <!-- Processing Overlay -->
      <div v-if="isProcessing" class="flex items-center justify-center py-16">
        <LoadingIcon />
        <span class="ml-12 type-sm text-text-subtle">{{ $t('checkout.norceCheckout.processing') }}</span>
      </div>

      <!-- Payment In Progress (409 - concurrent request blocked) -->
      <div v-if="showProcessingMessage" class="mt-16 p-16 bg-bg-warning rounded-8">
        <p class="type-sm">{{ $t('checkout.norceCheckout.paymentInProgress') }}</p>
      </div>

      <!-- Payment Error requiring deliberate retry (non-decline backend errors) -->
      <div v-else-if="requiresManualRetry && paymentErrorMessage" class="mt-16 p-16 bg-bg-error rounded-8">
        <p class="type-sm text-text-error">{{ paymentErrorMessage }}</p>
        <button
          class="btn btn--secondary mt-16"
          type="button"
          @click="handleTryAgain"
        >
          {{ $t('checkout.norceCheckout.retry') }}
        </button>
      </div>

      <!-- Payment Declined (Refused/Cancelled - Drop-in already re-enabled) -->
      <div v-else-if="error && payment" class="mt-16 p-16 bg-bg-error rounded-8">
        <p class="type-sm text-text-error">{{ error }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import useNorceCheckout from './useNorceCheckout';
import { savePaymentSession, getPaymentSession, clearPaymentSession, buildSuccessUrl } from './usePaymentSession';
import useLinkReplacer from '~/composables/useLinkReplacer';
import LoadingIcon from '~/components/generic/LoadingIcon.vue';
import { useGlobalContentStore } from '~/store/globalContent';
import { useCartStore } from '~/store/cart';
import type { AdyenTransactionData, AdyenPaymentData, AdyenAction, NorceOrderResult } from './types';

// Types for Adyen Web SDK
type AdyenCheckoutInstance = {
  update: (config: Record<string, unknown>) => void;
};

type AdyenDropinInstance = {
  mount: (element: HTMLElement | string) => AdyenDropinInstance;
  unmount: () => void;
  handleAction: (action: unknown) => void;
};

type AdyenState = {
  data: AdyenTransactionData;
  isValid: boolean;
};

type AdyenComponent = {
  handleAction: (action: unknown) => void;
  setStatus: (status: 'loading' | 'ready' | 'success' | 'error') => void;
};

// Adyen Web v6 advanced-flow callbacks receive an `actions` object.
// The result MUST be handed back via resolve()/reject(), otherwise wallet
// payment sheets (Google Pay / Apple Pay) never close and time out with REQUEST_TIMEOUT.
type AdyenActions = {
  resolve: (response: { resultCode: string; action?: unknown; order?: unknown; donationToken?: string }) => void;
  reject: () => void;
};

const props = withDefaults(defineProps<{
  basketUuid: string;
  // When false, shows validation overlay blocking Adyen Drop-in interaction
  canCompletePurchase?: boolean;
  // Whether to auto-initialize on mount. Set to false when used inside NorceCheckoutPayment
  // which handles initialization itself.
  autoInitialize?: boolean;
}>(), {
  canCompletePurchase: true,
  autoInitialize: true,
});

const emit = defineEmits<{
  (e: 'success', basketId: string): void;
  (e: 'error', error: string): void;
}>();

const { $t } = useNuxtApp();
const { manualUrl } = useLinkReplacer();
const globalContent = useGlobalContentStore();
const cartStore = useCartStore();
const { checkoutVersion } = storeToRefs(cartStore);

const {
  orderId,
  paymentId,
  payment,
  isInitializing,
  isProcessing,
  isCheckingStatus,
  error,
  isAdyen,
  initialize,
  submitPayment,
  submitDetails,
  refresh,
  checkPaymentStatus,
  reset,
} = useNorceCheckout();

const route = useRoute();

const dropinContainer = ref<HTMLElement | null>(null);
let checkout: AdyenCheckoutInstance | null = null;
let dropin: AdyenDropinInstance | null = null;
const initialCheckoutVersion = ref<number | null>(null);
const isRefreshing = ref(false);
const isDropinReady = ref(false); // Track when dropin is fully initialized
let statusCheckTimeout: ReturnType<typeof setTimeout> | null = null;

// Payment submission lock state
const paymentComponent = ref<AdyenComponent | null>(null);
const paymentLocked = ref(false); // blocks Drop-in re-submission via CSS overlay
const showProcessingMessage = ref(false); // 409: payment already in progress
const requiresManualRetry = ref(false); // non-decline error: show Try Again button
const paymentErrorMessage = ref<string | null>(null); // message shown in Try Again block

// Debounce delay for status check (allows backend to sync changes)
const STATUS_CHECK_DEBOUNCE_MS = 1500;

// Typed payment helper (local to this component)
const adyenPayment = computed(() => isAdyen.value ? payment.value as AdyenPaymentData : null);

// Processing redirect result state
const isProcessingRedirect = ref(false);

/**
 * Save current payment session for redirect flow
 */
const saveCurrentSession = () => {
  savePaymentSession({
    orderId: orderId.value || undefined,
    paymentId: paymentId.value || undefined,
    basketUuid: props.basketUuid,
    paymentAdapter: 'adyen',
  });
};

/**
 * Handle redirectResult from 3DS redirect flow
 * Called when user returns from bank authentication
 */
const handleRedirectResult = async(redirectResult: string) => {
  isProcessingRedirect.value = true;

  try {
    // Get saved session or use current values
    const session = getPaymentSession();
    const currentOrderId = orderId.value || session.orderId;
    const currentPaymentId = paymentId.value || session.paymentId;
    const basketUuid = props.basketUuid || session.basketUuid;

    if (!currentOrderId || !currentPaymentId) {
      // Try to initialize first to get orderId/paymentId
      if (basketUuid) {
        await initialize(basketUuid);
      }

      if (!orderId.value || !paymentId.value) {
        emit('error', 'Unable to complete payment - session expired. Please try again.');
        clearPaymentSession();
        return;
      }
    }

    // Submit the redirect result to complete 3DS
    const result = await submitDetails({ redirectResult });

    // Clear saved session
    clearPaymentSession();

    if (result.success) {
      const successBasketId = result.order?.basketId || basketUuid || props.basketUuid;
      emit('success', successBasketId);
      // Use unified success URL with payment context
      const successUrl = buildSuccessUrl(manualUrl(''), successBasketId, {
        adapter: 'adyen',
        orderId: currentOrderId || orderId.value,
        paymentId: currentPaymentId || paymentId.value,
      });
      await navigateTo(successUrl);
    } else if (result.action) {
      // Another action required (rare but possible)
      const action = (result.action as any).actualInstance || result.action;
      if (action?.type && dropin) {
        dropin.handleAction(action);
      } else {
        emit('error', error.value || 'Payment requires additional action');
      }
    } else {
      emit('error', error.value || 'Payment authentication failed');
    }
  } catch (e) {
    console.error('Error handling redirect result:', e);
    emit('error', 'Failed to complete payment authentication');
    clearPaymentSession();
  } finally {
    isProcessingRedirect.value = false;
  }
};

/**
 * Initialize Adyen Drop-in
 */
const initializeAdyen = async() => {
  if (!adyenPayment.value) {
    return;
  }

  if (!dropinContainer.value) {
    // Container not ready - wait for next tick and retry once
    await nextTick();
    if (!dropinContainer.value) {
      emit('error', 'Payment form container not available. Please refresh the page.');
      return;
    }
  }

  try {
    // Dynamic import of Adyen Web SDK
    const adyenWeb = await import('@adyen/adyen-web');
    const { AdyenCheckout, Dropin } = adyenWeb;

    // Import Adyen CSS
    await import('@adyen/adyen-web/styles/adyen.css');

    // Map payment method types to Adyen component names
    const componentMap: Record<string, string> = {
      scheme: 'Card',
      klarna: 'Klarna',
      klarna_account: 'Klarna',
      klarna_paynow: 'Klarna',
      swish: 'Swish',
      trustly: 'Trustly',
      paysafecard: 'Paysafecard',
      ideal: 'Ideal',
      giropay: 'Giropay',
      sofort: 'Redirect',
      paypal: 'PayPal',
      applepay: 'ApplePay',
      googlepay: 'GooglePay',
      bancontact: 'Bancontact',
      eps: 'EPS',
      multibanco: 'Multibanco',
    };

    // Get unique component names needed based on available payment methods
    const paymentMethods = adyenPayment.value.paymentMethodsResponse?.paymentMethods || [];
    const neededComponents = [...new Set(
      paymentMethods
        .map(pm => componentMap[pm.type])
        .filter(Boolean)
    )];

    // Load the required payment method components
    const paymentMethodComponents = neededComponents
      .map(name => (adyenWeb as Record<string, unknown>)[name])
      .filter(Boolean);

    // Get countryCode from response or fallback to globalContent (uppercase for Adyen)
    const countryCode = adyenPayment.value.countryCode || globalContent.getCountryCode?.toUpperCase() || 'SE';

    const returnUrl = `${window.location.origin}${manualUrl('/checkout/payment-redirect')}`;

    const configuration = {
      paymentMethodsResponse: adyenPayment.value.paymentMethodsResponse,
      clientKey: adyenPayment.value.clientKey,
      environment: adyenPayment.value.environment,
      amount: adyenPayment.value.amount,
      countryCode,
      locale: adyenPayment.value.locale,
      onSubmit: handleOnSubmit,
      onAdditionalDetails: handleOnAdditionalDetails,
      onError: handleOnError,
    };

    checkout = await AdyenCheckout(configuration);
    dropin = new Dropin(checkout, {
      openFirstPaymentMethod: true,
      openFirstStoredPaymentMethod: false,
      showStoredPaymentMethods: true,
      showPaymentMethods: true,
      showPayButton: true,
      paymentMethodComponents,
      paymentMethodsConfiguration: {
        card: { returnUrl },
        klarna: { returnUrl },
        klarna_account: { returnUrl },
        klarna_paynow: { returnUrl },
        ideal: { returnUrl },
        sofort: { returnUrl },
        paypal: { returnUrl },
        googlepay: { returnUrl },
        applepay: { returnUrl },
      },
    });

    dropin.mount(dropinContainer.value);
    isDropinReady.value = true;
  } catch (e) {
    console.error('Failed to initialize Adyen Drop-in:', e);
    emit('error', 'Failed to load payment form');
  }
};

/**
 * Resolve/reject the Adyen v6 flow, then handle navigation/errors on our side.
 *
 * Adyen Web v6 drives 3DS/redirect actions itself from what we resolve here, so we
 * must NOT call component.handleAction() manually. Handing the result back via
 * actions.resolve()/reject() is also what closes the Google Pay / Apple Pay wallet
 * sheet — skipping it leaves the sheet spinning until it errors with REQUEST_TIMEOUT.
 *
 * Locking strategy (v6): always call resolve/reject to close wallet sheets, then
 * set paymentLocked=true overlay for errors that should not allow re-submission.
 * Genuine declines (Refused/Cancelled) skip the lock — SDK re-enables for retry.
 */
const finishAdyenFlow = async(
  result: { success: boolean; resultCode?: string; action?: AdyenAction; order?: NorceOrderResult; is409?: boolean; canRetry?: boolean },
  actions: AdyenActions,
  failMessage: string
) => {
  // 409: close wallet sheet but lock Drop-in — payment already in progress on backend
  if (result.is409) {
    actions.reject();
    paymentLocked.value = true;
    showProcessingMessage.value = true;
    return;
  }

  if (result.resultCode) {
    actions.resolve({
      resultCode: result.resultCode,
      // Pass the action (if any) so the SDK handles 3DS/redirect internally
      action: result.action?.actualInstance || result.action || undefined,
    });
  } else {
    // No result code means the request never produced a usable response
    actions.reject();
  }

  if (result.success) {
    const basketId = result.order?.basketId || props.basketUuid;
    emit('success', basketId);
    const successUrl = buildSuccessUrl(manualUrl(''), basketId, {
      adapter: 'adyen',
      orderId: orderId.value,
      paymentId: paymentId.value,
    });
    await navigateTo(successUrl);
  } else if (result.action) {
    // SDK continues the flow via onAdditionalDetails — no lock needed
  } else if (result.canRetry) {
    // Genuine decline (Refused/Cancelled) — SDK re-enables the Drop-in, no lock
    emit('error', error.value || failMessage);
  } else {
    // Any other terminal failure — lock Drop-in and require deliberate retry
    const msg = error.value || failMessage;
    paymentLocked.value = true;
    requiresManualRetry.value = true;
    paymentErrorMessage.value = msg;
    emit('error', msg);
  }
};

/**
 * Re-enable the Drop-in after a non-decline error.
 * Called only by the explicit "Try again" button — never automatically.
 */
const handleTryAgain = () => {
  paymentLocked.value = false;
  requiresManualRetry.value = false;
  showProcessingMessage.value = false;
  paymentErrorMessage.value = null;
};

/**
 * Handle payment submission from Adyen Drop-in (Adyen Web v6 advanced flow)
 */
const handleOnSubmit = async(state: AdyenState, _component: AdyenComponent, actions: AdyenActions) => {
  if (!state.isValid) {
    actions.reject();
    return;
  }

  const result = await submitPayment(state.data);
  await finishAdyenFlow(result, actions, 'Payment failed');
};

/**
 * Handle additional details (3DS authentication, Swish, etc.) (Adyen Web v6 advanced flow)
 */
const handleOnAdditionalDetails = async(state: AdyenState, _component: AdyenComponent, actions: AdyenActions) => {
  const result = await submitDetails(state.data as unknown as Record<string, unknown>);
  await finishAdyenFlow(result, actions, 'Authentication failed');
};

/**
 * Handle Adyen Drop-in errors
 */
const handleOnError = (errorObj: { message: string }) => {
  console.error('Adyen Drop-in error:', errorObj);
  emit('error', errorObj.message || 'Payment error occurred');
};

/**
 * Retry initialization
 */
const retryInitialize = async() => {
  showProcessingMessage.value = false;
  requiresManualRetry.value = false;
  paymentErrorMessage.value = null;
  paymentComponent.value = null;
  reset();
  await initialize(props.basketUuid);

  if (isAdyen.value && adyenPayment.value) {
    await initializeAdyen();
  }
};

/**
 * Unmount Adyen Drop-in when switching adapters
 */
const unmountAdyen = () => {
  isDropinReady.value = false;
  if (dropin) {
    dropin.unmount();
    dropin = null;
  }
  checkout = null;
};

/**
 * Debounced check for payment status changes
 * Called when checkoutVersion changes (cart modifications)
 */
const debouncedCheckPaymentStatus = async() => {
  // Clear any pending status check
  if (statusCheckTimeout) {
    clearTimeout(statusCheckTimeout);
    statusCheckTimeout = null;
  }

  // Schedule new status check with debounce
  statusCheckTimeout = setTimeout(async() => {
    isRefreshing.value = true;

    try {
      const result = await checkPaymentStatus();

      if (result.success && isAdyen.value && adyenPayment.value && checkout) {
        // Update the Drop-in with latest payment data
        await updateDropin();
      }
    } catch (e) {
      console.error('Failed to check payment status:', e);
    } finally {
      isRefreshing.value = false;
      statusCheckTimeout = null;
    }
  }, STATUS_CHECK_DEBOUNCE_MS);
};

/**
 * Update Adyen Drop-in when payment methods change
 */
const updateDropin = async() => {
  if (!checkout || !adyenPayment.value || !isDropinReady.value) {
    return;
  }

  try {
    checkout.update({
      paymentMethodsResponse: adyenPayment.value.paymentMethodsResponse,
      amount: adyenPayment.value.amount,
    });
  } catch (e) {
    console.error('Failed to update Adyen Drop-in:', e);
  }
};

// Watch for adyenPayment changes to initialize/update the Drop-in
watch(adyenPayment, async(newValue, oldValue) => {
  if (newValue && !oldValue) {
    // First time initialization - skip if still initializing (container won't exist)
    if (isInitializing.value) {
      return; // Will be handled by isInitializing watcher below
    }
    await nextTick();
    await initializeAdyen();
  } else if (newValue && oldValue && checkout) {
    // Update existing Drop-in
    await updateDropin();
  }
});

// Watch for isInitializing to become false - then initialize dropin if we have payment data
watch(isInitializing, async(newValue, oldValue) => {
  if (oldValue && !newValue && adyenPayment.value && !checkout) {
    // Initialization just finished, we have payment data, but dropin not mounted yet
    await nextTick();
    await initializeAdyen();
  }
});

// Watch for checkout changes (quantity, shipping, etc.) and check payment status
watch(checkoutVersion, async() => {
  // Skip if this is the initial version capture or if we're already refreshing/checking
  if (initialCheckoutVersion.value === null || isRefreshing.value || isCheckingStatus.value) {
    return;
  }

  // Only check status if we have an active Adyen payment session
  if (isAdyen.value && adyenPayment.value) {
    await debouncedCheckPaymentStatus();
  }
});

onMounted(async() => {
  // Check for redirectResult from 3DS redirect flow (fallback if user lands here instead of /checkout/payment-redirect)
  const redirectResult = route.query.redirectResult as string | undefined;

  if (redirectResult) {
    // User is returning from 3DS authentication - handle it
    await handleRedirectResult(redirectResult);
    // Clear the query parameter from URL without navigation
    const newQuery = { ...route.query };
    delete newQuery.redirectResult;
    await navigateTo({ query: newQuery }, { replace: true });
    return;
  }

  // Capture the initial checkout version to detect future changes
  initialCheckoutVersion.value = checkoutVersion.value;

  // Skip initialization if disabled (when used inside NorceCheckoutPayment which handles it)
  if (!props.autoInitialize) {
    saveCurrentSession();
    // Component mounts after parent's initialize() completes (due to v-else-if condition)
    // So we need to initialize dropin here since watchers won't fire
    if (adyenPayment.value && !checkout) {
      await nextTick();
      await initializeAdyen();
    }
    return;
  }

  // Initialize checkout session
  const success = await initialize(props.basketUuid);

  if (success) {
    // Save session for 3DS redirect flow (used by /checkout/payment-redirect page)
    saveCurrentSession();

    if (isAdyen.value && adyenPayment.value) {
      // Wait for template to update after isInitializing becomes false
      await nextTick();
      await initializeAdyen();
    }
  }
});

onBeforeUnmount(() => {
  // Clear any pending status check
  if (statusCheckTimeout) {
    clearTimeout(statusCheckTimeout);
    statusCheckTimeout = null;
  }

  // Cleanup Adyen dropin only - don't reset checkout state
  // (state is managed by parent or when explicitly switching adapters)
  unmountAdyen();
});

// Expose methods for parent components
defineExpose({
  refresh,
  checkPaymentStatus,
});
</script>

<style scoped lang="postcss">
.norce-checkout-dropin {
  @apply w-full;
}

.adyen-dropin-wrapper {
  @apply relative;
}

.adyen-dropin-container {
  @apply w-full;
}

/* Validation overlay - blocks interaction until checkout requirements are met */
.adyen-validation-overlay {
  @apply absolute inset-0 cursor-not-allowed z-10;
  background-color: rgba(255, 255, 255, 0.6);
}

/* Adyen Drop-in styling overrides */
:deep(.adyen-checkout__dropin) {
  @apply font-open;
}

:deep(.adyen-checkout__payment-method) {
  @apply border border-border-subtle rounded-8;
}

:deep(.adyen-checkout__payment-method--selected) {
  @apply border-border-base;
}

:deep(.adyen-checkout__button--pay) {
  @apply bg-bg-brand text-text-inverse rounded-8;
}

:deep(.adyen-checkout__button--pay:hover) {
  @apply bg-brandDark;
}
</style>
